/**
 * Admin Logs Section
 *
 * Displays exception logs for system administrators.
 */

import { useState } from 'react';
import {
  useAdminExceptionLogs,
  useAdminLogStats,
  useAdminErrorCodes,
} from '../../hooks/useAdminCosts';
import type { ExceptionLogItem } from '../../types';

// Error code severity colors
const ERROR_CODE_COLORS: Record<string, string> = {
  EXTERNAL_API_ERROR: 'bg-orange-100 text-orange-800',
  RATE_LIMIT_ERROR: 'bg-yellow-100 text-yellow-800',
  AUTHENTICATION_ERROR: 'bg-red-100 text-red-800',
  AUTHORIZATION_ERROR: 'bg-red-100 text-red-800',
  VALIDATION_ERROR: 'bg-blue-100 text-blue-800',
  RESOURCE_NOT_FOUND: 'bg-gray-100 text-gray-800',
  KB_BUILD_ERROR: 'bg-purple-100 text-purple-800',
  DATA_INGESTION_ERROR: 'bg-amber-100 text-amber-800',
  STORAGE_ERROR: 'bg-red-100 text-red-800',
  CONFIGURATION_ERROR: 'bg-red-100 text-red-800',
  DOSSIER_ERROR: 'bg-red-100 text-red-800',
};

function getErrorCodeColor(errorCode: string): string {
  return ERROR_CODE_COLORS[errorCode] || 'bg-gray-100 text-gray-800';
}

function formatTimestamp(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

interface LogStatCardProps {
  label: string;
  value: number | string;
  color: string;
}

function LogStatCard({ label, value, color }: LogStatCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

interface ExceptionLogRowProps {
  log: ExceptionLogItem;
  isExpanded: boolean;
  onToggle: () => void;
}

function ExceptionLogRow({ log, isExpanded, onToggle }: ExceptionLogRowProps) {
  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3 text-sm text-gray-500">
          <div>{formatTimestamp(log.timestamp)}</div>
          <div className="text-xs text-gray-400">
            {formatRelativeTime(log.timestamp)}
          </div>
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getErrorCodeColor(
              log.error_code
            )}`}
          >
            {log.error_code}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-900 max-w-md truncate">
          {log.message}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">
          {log.source}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {log.request_method && log.request_path && (
            <span className="font-mono text-xs">
              {log.request_method} {log.request_path}
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-center">
          <button className="text-gray-400 hover:text-gray-600">
            {isExpanded ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-4 py-4">
            <div className="space-y-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Full Message
                </h4>
                <p className="text-sm text-gray-900">{log.message}</p>
              </div>

              {Object.keys(log.details).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                    Details
                  </h4>
                  <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(log.details, null, 2)}
                  </pre>
                </div>
              )}

              {log.traceback && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                    Traceback
                  </h4>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto font-mono">
                    {log.traceback}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface ExceptionLogsTableProps {
  logs: ExceptionLogItem[];
  isLoading: boolean;
  error: Error | null;
}

function ExceptionLogsTable({ logs, isLoading, error }: ExceptionLogsTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">
        Error loading logs: {error.message}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center">
        No exception logs found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Time
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Error Code
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Message
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Source
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Request
            </th>
            <th className="px-4 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {logs.map((log) => (
            <ExceptionLogRow
              key={log.id}
              log={log}
              isExpanded={expandedId === log.id}
              onToggle={() =>
                setExpandedId(expandedId === log.id ? null : log.id)
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminLogsSection() {
  const [errorCodeFilter, setErrorCodeFilter] = useState<string>('');
  const [limit, setLimit] = useState(100);

  const logsQuery = useAdminExceptionLogs(
    limit,
    errorCodeFilter || undefined
  );
  const statsQuery = useAdminLogStats();
  const errorCodesQuery = useAdminErrorCodes();

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Exception Logs</h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <LogStatCard
          label="Errors Today"
          value={statsQuery.data?.total_today ?? 0}
          color={
            (statsQuery.data?.total_today ?? 0) > 0
              ? 'text-red-600'
              : 'text-green-600'
          }
        />
        <LogStatCard
          label="Errors (7 Days)"
          value={statsQuery.data?.total_7_days ?? 0}
          color="text-gray-900"
        />
        <LogStatCard
          label="Most Common"
          value={statsQuery.data?.most_common ?? 'None'}
          color="text-gray-900"
        />
        <LogStatCard
          label="Error Types"
          value={Object.keys(statsQuery.data?.by_error_code ?? {}).length}
          color="text-gray-900"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div>
          <label
            htmlFor="errorCode"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Filter by Error Code
          </label>
          <select
            id="errorCode"
            value={errorCodeFilter}
            onChange={(e) => setErrorCodeFilter(e.target.value)}
            className="rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All error codes</option>
            {errorCodesQuery.data?.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="limit"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Show
          </label>
          <select
            id="limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value={50}>50 entries</option>
            <option value={100}>100 entries</option>
            <option value={200}>200 entries</option>
            <option value={500}>500 entries</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <ExceptionLogsTable
          logs={logsQuery.data ?? []}
          isLoading={logsQuery.isLoading}
          error={logsQuery.error}
        />
      </div>
    </section>
  );
}

export default AdminLogsSection;
